import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { fitScale, PREVIEW_REFERENCE_WIDTH } from './fitScale.js';

/**
 * Shrinks the preview to fit its column, at true proportions.
 *
 * 🚨 This is what makes the Designer honest on a phone. Reflowing to fit — which is what happened
 * before, because the panel is `width: 100%; max-width: 460px` — showed the merchant a 302px
 * layout no buyer ever gets: prices broken mid-number, a placeholder spilling out of its input,
 * the submit label wrapped inside its button. Scaling shows the TRUE layout, smaller.
 *
 * So the content lays out at `PREVIEW_REFERENCE_WIDTH` regardless of the column, and the whole box is
 * transformed down to whatever room there is. `transform-origin: top left` keeps it anchored, and
 * the outer div takes the SCALED height because a transform does not affect layout — without it
 * the card reserves the unscaled height and leaves a long gap under the preview.
 *
 * Never scales UP: on a desktop column wider than 460px, `min(1, …)` leaves it alone, so nothing
 * about the desktop Designer changes.
 */
export function ScaleToFit({
  children,
  reference = PREVIEW_REFERENCE_WIDTH,
  center = false,
}: {
  children: ReactNode;
  /** The width the content is laid out at. Defaults to the modal panel; the thank-you page is
   *  narrower, because it renders INSIDE the form's own padding. */
  reference?: number;
  /**
   * Centre the fixed-width box in a column wider than it.
   *
   * Opt-in, because the two callers sit in different places: the Designer's preview column is
   * about the reference width already, so there is nothing to centre and pinning it left keeps it
   * beside the controls it belongs to. The Settings card is full-page width, where a 404px page
   * left-aligned in a 700px box reads as a layout mistake. `margin: auto` works at any zoom —
   * `zoom` scales the layout box, so the auto margins are computed from the scaled width.
   */
  center?: boolean;
}): JSX.Element {
  const outer = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  /**
   * 🚨 Three rules keep this from becoming an infinite render loop, which is exactly what the
   * first version did — "Maximum update depth exceeded", thrown from the editor on a phone and
   * from the Designer on any mode toggle:
   *
   *  1. **The effect runs ONCE.** It had no dependency array, so it re-ran after every render and
   *     called `measure()` synchronously — one state change was enough to re-enter it forever.
   *  2. **It observes the OUTER box only.** Observing the inner one closed the circle: our own
   *     output changed the inner's box, which re-fired the observer that produced it.
   *  3. **It stores only the SCALE, derived from a width we never write to.** The first version
   *     also measured the content height and pushed it back into `style.height`; the preview
   *     rebuilds its subtree on every render, so that height kept arriving slightly different and
   *     the guard never converged.
   *
   * The width is quantised so a scrollbar appearing or disappearing — a ~15px step — cannot flip
   * the scale back and forth across a boundary.
   */
  useLayoutEffect(() => {
    const outerEl = outer.current;
    if (!outerEl) return;
    const measure = (): void => {
      const available = Math.floor(outerEl.clientWidth / 8) * 8;
      setScale((prev) => {
        const next = fitScale(available, reference);
        return Math.abs(prev - next) < 0.01 ? prev : next;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outerEl);
    return () => ro.disconnect();
  }, [reference]);

  // 🚨 `zoom`, not `transform`. A transform does not affect layout, so the box kept its unscaled
  // height and the card reserved a screen of empty space below the preview — which is what the
  // measured height existed to correct, and that measurement is what looped. `zoom` scales the
  // layout itself, so the height follows with no JavaScript at all.
  //
  // Where `zoom` is unsupported (Firefox before 126) the transform is the fallback: the preview is
  // still readable and still true to proportion, it just leaves whitespace under itself. A wrong
  // gap beats a form the merchant cannot read — and beats a loop.
  const zoomable = typeof CSS !== 'undefined' && CSS.supports?.('zoom', '0.5');
  const shrink = scale < 1;

  return (
    <div ref={outer} style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <div
        style={{
          width: reference,
          ...(center ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
          ...(shrink && zoomable ? { zoom: scale } : {}),
          ...(shrink && !zoomable
            ? { transform: `scale(${scale})`, transformOrigin: 'top left' }
            : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}
